import React, { useState } from 'react';

const TravelAndStay: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const travelInfo = [
    {
      question: "Hotel Arrangements",
      answer: "Don't worry about booking accommodation - we've got you covered! Our wedding planner will coordinate directly with you regarding room assignments and check-in details."
    },
    {
      question: "Transportation",
      answer: "Airport and Train Station transfers from Khajuraho airport/train station to the hotel will be provided. Once flight details are confirmed, our Events team will coordinate pickup logistics to ensure smooth transportation to the hotel."
    },
    {
      question: "Arrival Recommendation",
      answer: "We suggest arriving on the morning of March 13th. We've intentionally kept things simple that day. Get there, enjoy great food, rest up, and be ready for our Sangeet!"
    },
    {
      question: "International Travelers",
      answer: "The best route is to fly to New Delhi, followed by a connecting flight to Khajuraho or drive from Delhi. If you're arriving in India early though, let us know and we'll help recommend best options. Ticket bookings for the Delhi-Khajuraho flight sector only open in October, so we recommend booking your international leg separate to that."
    },
    {
      question: "Domestic Travelers",
      answer: "Direct flights to Khajuraho are available from Delhi and Varanasi starting in October. Alternatively, you can fly to Jabalpur, M.P. and drive (4-5 hours). If you prefer to travel by Train, the Vande Bharat train travels directly from Delhi to Khajuraho once a day."
    },
    {
      question: "Things to Do in Khajuraho",
      answer: (
        <div>
          <p className="text-text-gray mb-4 leading-relaxed">
            For those arriving early or staying longer, Khajuraho offers incredible experiences. Please reach out to Shivangi or Ishan if you're interested in any of these and we can help coordinate.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-base font-display text-soft-black mb-2 tracking-wide">Historical Sites</h3>
              <ul className="space-y-1 text-text-gray text-sm">
                <li>• Khajuraho Group of Monuments (UNESCO World Heritage Site)</li>
                <li>• Western Group of Temples</li>
                <li>• Eastern Group of Temples</li>
                <li>• Archaeological Museum</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-base font-display text-soft-black mb-2 tracking-wide">Natural Attractions</h3>
              <ul className="space-y-1 text-text-gray text-sm">
                <li>• Panna National Park (45 minutes away)</li>
                <li>• Raneh Falls (20 minutes away)</li>
                <li>• Ken Gharial Sanctuary</li>
                <li>• Sunset Point at Khajuraho</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-base font-display text-soft-black mb-2 tracking-wide">Cultural Experiences</h3>
              <ul className="space-y-1 text-text-gray text-sm">
                <li>• Light and Sound Show at the Khajuraho Temples</li>
                <li>• Local handicraft shopping</li>
                <li>• Traditional dance performances</li>
                <li>• Village tours and rural experiences</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-base font-display text-soft-black mb-2 tracking-wide">Adventure Activities</h3>
              <ul className="space-y-1 text-text-gray text-sm">
                <li>• Jungle Safari at Panna National Park</li>
                <li>• Boating and sightseeing on Ken River</li>
                <li>• Rock climbing at Raneh Falls</li>
                <li>• Cycling tours around temples</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ];

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-display font-medium text-sage-800 mb-4">
          Travel & Stay
        </h1>
        <div className="elegant-divider"></div>
        <p className="text-sage-grey-600">
          Everything you need to know for your journey to Khajuraho.
        </p>
        {/* Photo */}
        <div className="photo-frame max-w-xl mx-auto mt-8 mb-8" style={{width: '85%', height: '400px', overflow: 'hidden', display: 'block', backgroundColor: 'transparent'}}>
          {/* Desktop/Tablet Image */}
          <img
              src="/images/travel-and-stay/0N7A8849_optimized.jpg"
              alt="Shivangi and Ishan"
              className="w-full h-full object-cover hidden sm:block"
              style={{ objectFit: 'cover', height: '100%', maxWidth: 'none' }}
              loading="lazy"
              decoding="async"
          />
          {/* Mobile Image */}
          <img
              src="/images/travel-and-stay/IMG_1076_optimized.jpg"
              alt="Shivangi and Ishan"
              className="w-full h-full object-cover block sm:hidden"
              style={{ filter: 'contrast(0.8) brightness(0.9)' }}
              loading="lazy"
              decoding="async"
          />
        </div>
      </div>

      {/* Travel Information Toggles */}
      <div className="space-y-1">
        {travelInfo.map((info, index) => (
          <div key={index} className="glass-card overflow-hidden">
            <button
              onClick={() => toggleQuestion(index)}
              className="w-full p-6 text-left flex items-center justify-between hover:bg-white hover:bg-opacity-20 transition-colors duration-300 min-h-[60px]"
            >
              <h2 className="text-lg font-display text-soft-black pr-4 tracking-wide font-normal">
                {info.question}
              </h2>
              <div className={`transform transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                <svg className="w-5 h-5 text-text-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-6 pb-6">
                <div className="pt-2 border-t border-mid-gray">
                  {typeof info.answer === 'string' ? (
                    <p className="text-text-gray leading-relaxed">
                      {info.answer}
                    </p>
                  ) : (
                    info.answer
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default TravelAndStay;